import { demoFunc } from './dees-harness-content-blocks.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import type { THarnessContentBlock } from '../interfaces.js';
import { harnessFormatBytes } from '../harness.helpers.js';
import { harnessMonoStyles } from '../harness.styles.js';
import '../../00group-utility/dees-icon/dees-icon.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-content-blocks': DeesHarnessContentBlocks;
  }
}

/**
 * Structural renderer for MCP CallToolResult content blocks: text, image,
 * audio, embedded resources, and resource links keep their nature instead of
 * being flattened to strings.
 */
@customElement('dees-harness-content-blocks')
export class DeesHarnessContentBlocks extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property({ attribute: false })
  accessor blocks: THarnessContentBlock[] = [];

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    harnessMonoStyles,
    css`
      :host {
        display: grid;
        gap: var(--dees-spacing-sm);
        font-family: var(--dees-font-family);
      }

      .textBlock {
        color: var(--dees-color-text-primary);
        white-space: pre-wrap;
        overflow-wrap: anywhere;
        font-size: var(--dees-font-control-size-sm, 12px);
        line-height: 1.5;
      }

      img {
        display: block;
        max-width: 100%;
        max-height: 420px;
        border-radius: var(--dees-radius-md);
        border: 1px solid var(--dees-color-border-subtle);
      }

      audio {
        width: 100%;
      }

      .resourceLink {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        padding: var(--dees-spacing-xs) var(--dees-spacing-sm);
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: var(--dees-radius-md);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-bg-tertiary);
        color: var(--dees-color-text-primary);
        text-decoration: none;
        font-size: var(--dees-font-control-size-sm, 12px);
      }

      .resourceLink:hover {
        background: var(--dees-color-hover);
      }

      .resourceLink dees-icon {
        color: var(--dees-color-accent-primary);
      }

      .resourceInfo {
        min-width: 0;
      }

      .resourceName {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-weight: 500;
      }

      .resourceMeta {
        margin-top: 1px;
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size, 11px);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .resourceUri {
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption1-size, 11px);
        font-family: var(--dees-font-family-mono);
        overflow-wrap: anywhere;
      }
    `,
  ];

  public render(): TemplateResult {
    return html`${this.blocks.map((block) => this.renderBlock(block))}`;
  }

  private renderBlock(block: THarnessContentBlock): TemplateResult {
    switch (block.type) {
      case 'text':
        return html`<div class="textBlock">${block.text}</div>`;
      case 'image':
        return html`<img src=${`data:${block.mimeType};base64,${block.data}`} alt="tool result image" />`;
      case 'audio':
        return html`<audio controls src=${`data:${block.mimeType};base64,${block.data}`}></audio>`;
      case 'resource': {
        const resource = block.resource;
        if (resource.text !== undefined) {
          return html`
            <div>
              <div class="resourceUri">${resource.uri}</div>
              <pre class="harness-mono">${resource.text}</pre>
            </div>
          `;
        }
        return html`
          <a
            class="resourceLink"
            href=${`data:${resource.mimeType ?? 'application/octet-stream'};base64,${resource.blob ?? ''}`}
            download
          >
            <dees-icon icon="lucide:FileDown" iconSize="16"></dees-icon>
            <div class="resourceInfo">
              <div class="resourceName">${resource.uri}</div>
              <div class="resourceMeta">${resource.mimeType ?? 'binary resource'}</div>
            </div>
          </a>
        `;
      }
      case 'resource_link':
        return html`
          <a class="resourceLink" href=${block.uri} target="_blank" rel="noopener noreferrer">
            <dees-icon icon="lucide:Link" iconSize="16"></dees-icon>
            <div class="resourceInfo">
              <div class="resourceName">${block.title || block.name}</div>
              <div class="resourceMeta">
                ${[block.description, block.mimeType, block.size !== undefined ? harnessFormatBytes(block.size) : '']
                  .filter(Boolean)
                  .join(' · ') || block.uri}
              </div>
            </div>
          </a>
        `;
      default:
        return html``;
    }
  }
}
